<template>
  <LayoutPage title="Incident & Task Management">
    <AlertsAnimated :alerts="localAlerts" />
    <template #actions>
      <div v-if="showEscalationActions" class="mr-3">
        <v-btn
          color="primary"
          class="mr-2"
          :loading="escalationSaving"
          @click="handleEscalationSave"
        >
          Save
        </v-btn>
        <v-btn
          color="grey"
          :disabled="escalationSaving"
          @click="handleEscalationCancel"
        >
          Cancel
        </v-btn>
      </div>
      <template v-if="!isFacilityDefaultOrganization">
        <v-btn color="success" @click="openAddTaskDialog"> ADD NEW TASK </v-btn>
      </template>
    </template>

    <div class="mt-4">
      <!-- Filters Row -->
      <v-row class="mb-4" align="start">
        <v-col cols="3">
          <v-select
            v-model="selectedTaskType"
            :items="taskTypeOptions"
            label="Task Type"
            outlined
            dense
            @change="handleTaskTypeChange"
          />
        </v-col>

        <v-col v-if="showSensorFilter" cols="3">
          <v-select
            v-model="selectedSensor"
            :items="sensorOptions"
            label="Sensor"
            outlined
            dense
          />
        </v-col>

        <v-col cols="3">
          <v-select
            v-model="selectedPriority"
            :items="priorityOptions"
            label="Priority"
            outlined
            dense
          />
        </v-col>

        <v-col cols="3">
          <v-text-field
            v-model="searchQuery"
            append-icon="mdi-magnify"
            label="Search"
            outlined
            dense
            hide-details
          />
        </v-col>
      </v-row>
      <div
        v-if="selectedTaskType === 'incident_resolution'"
        align="center"
        class="mb-5"
      >
        <v-btn
          :color="showPrioritySettings ? 'success' : 'grey lighten-1'"
          @click="togglePrioritySettings"
        >
          Priority Settings
        </v-btn>
        <v-btn
          :color="!showPrioritySettings ? 'success' : 'grey lighten-1'"
          @click="showPrioritySettings = false"
        >
          Escalation Matrix
        </v-btn>
      </div>

      <div v-if="selectedTaskType === 'incident_resolution'">
        <div v-if="showPrioritySettings">
          <div
            v-for="priority in priorities"
            :key="priority.name"
            class="accordion-item"
          >
            <v-card
              flat
              :class="{ 'mb-2': true, 'accordion-active': priority.isOpen }"
              @click="toggleAccordion(priority.name)"
            >
              <v-card-title
                class="accordion-header d-flex justify-space-between"
                :style="{ color: getColorBySeverity(priority.name) }"
              >
                {{ priority.title }}
                <v-icon>
                  {{ priority.isOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}
                </v-icon>
              </v-card-title>
            </v-card>

            <v-expand-transition>
              <div v-if="priority.isOpen" class="accordion-content">
                <v-card flat>
                  <v-card-text>
                    <CardPriorityContent
                      :name="priority.name"
                      :level="priority.level"
                      :settings="getSettingsForPriority(priority.name)"
                      :all-priority-data="priorityData"
                      :feature-id="priorityFeatureId"
                      :field-disabled="fieldDisabled"
                      @save-success="handleSaveSuccess"
                      @save-error="handleSaveError"
                      @view-assigned-incidents="handleViewAssignedIncidents"
                    />
                  </v-card-text>
                </v-card>
              </div>
            </v-expand-transition>
          </div>
        </div>

        <div v-else>
          <CardIncidentEscalationMatrix
            ref="escalationMatrix"
            :initial-sensor="selectedSensor"
            :initial-priority="selectedPriority"
            :priority-matrix="priorityMatrix"
            :feature-id="escalationFeatureId"
            :field-disabled="fieldDisabled"
            @changes-detected="handleEscalationChanges"
            @save-success="handleEscalationSaveSuccess"
            @save-error="handleEscalationSaveError"
          />
        </div>
      </div>

      <div v-else>
        <v-card>
          <v-data-table
            :headers="taskRuleHeaders"
            :items="filteredTaskRules"
            :search="searchQuery"
            class="elevation-1"
            :loading="loading"
          >
            <template #item.code="{ item }">
              {{ toUpperCase(item.code) }}
            </template>

            <template #item.taskName="{ item }">
              {{ titleCase(item.name) }}
            </template>
            <template #item.facilityName="{ item }">
              {{ getFacilityName(item) }}
            </template>

            <template #item.schedule="{ item }">
              {{ formatScheduleForItem(item) }}
            </template>

            <template #item.detectedThreshold="{ item }">
              <div style="width: 200px">
                {{ formatDetectedThresholdForItem(item) }}
              </div>
            </template>

            <template #item.priority="{ item }">
              <v-chip :color="getColorBySeverity(item.priority)" dark>
                {{ titleCase(item.priority) }}
              </v-chip>
            </template>

            <template #item.status="{ item }">
              <v-chip
                outlined
                :color="item.status === 'active' ? 'success' : 'red'"
                dark
                small
              >
                {{ titleCase(item.status) }}
              </v-chip>
            </template>

            <template #item.notificationPreference="{ item }">
              <v-btn
                outlined
                color="primary"
                small
                @click="viewNotificationSettings(item)"
              >
                View
              </v-btn>
            </template>

            <template #item.actions="{ item }">
              <v-btn
                v-if="!item.isDefault"
                size="x-large"
                color="primary"
                icon
                :disabled="isFacilityDefaultOrganization"
                @click="editTaskRule(item)"
              >
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on">mdi-pencil</v-icon>
                  </template>
                  <span>Edit Task</span>
                </v-tooltip>
              </v-btn>

              <v-btn
                size="x-large"
                :color="item.status === 'active' ? 'green' : 'red'"
                icon
                :disabled="isFacilityDefaultOrganization"
                @click="toggleTaskRule(item)"
              >
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on">
                      {{
                        item.status === "active"
                          ? "mdi-toggle-switch"
                          : "mdi-toggle-switch-off"
                      }}
                    </v-icon>
                  </template>
                  <span>
                    {{
                      item.status === "active"
                        ? "Deactivate Task"
                        : "Activate Task"
                    }}
                  </span>
                </v-tooltip>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </div>
    </div>

    <DialogTaskRule
      v-model="showTaskDialog"
      :task-rule="selectedTaskRule"
      :is-editing="isEditing"
      @task-saved="handleTaskSaved"
      @show-alert="handleShowAlert"
    />

    <DialogLayout
      v-model="showNotificationDialog"
      title="Notification Settings"
      width="1400px"
    >
      <NotificationModal
        :value="selectedNotificationSettings"
        :notification-type="
          selectedTaskType === 'incident_resolution'
            ? 'incident_resolution'
            : 'task_rule'
        "
        :read-only="true"
      />
      <v-card-actions>
        <v-spacer />
        <v-btn @click="showNotificationDialog = false">Close</v-btn>
      </v-card-actions>
    </DialogLayout>
  </LayoutPage>
</template>

<script>
import CardIncidentEscalationMatrix from "./CardIncidentEscalationMatrix.vue";
import CardPriorityContent from "./CardPriorityContent.vue";
import DialogTaskRule from "./DialogTaskRule.vue";
import NotificationModal from "./NotificationModal.vue";
import { GET_OVERRIDDEN_ORGANIZATION_FEATURE } from "./graphql";
import { ORGANIZATION_FEATURES } from "../../graphql/organizations";
import {
  GET_TASK_RULES,
  CREATE_TASK_RULE,
  TASK_TYPES,
  UPDATE_TASK_RULE,
} from "./graphql";
import useAlerts from "@tod-ui/composables/useAlerts";
import LayoutPage from "@layout/LayoutPage.vue";
import { getColorBySeverity } from "../dashboard/helper";
import { snakeCase, titleCase, toUpperCase } from "@tod-ui/helpers/strings";
import useUser from "../authentication/useUser";
import DialogLayout from "@layout/DialogLayout.vue";
import useDashboard from "@components/dashboard/useDashboard";
import AlertsAnimated from "@tod-ui/components/AlertsAnimated.vue";
import { DEFAULT_MANAGE_OVERLOCKS_RULES } from "./helper";

export default {
  name: "PageEscalationMatrix",
  components: {
    CardIncidentEscalationMatrix,
    CardPriorityContent,
    DialogTaskRule,
    NotificationModal,
    LayoutPage,
    DialogLayout,
    AlertsAnimated,
  },
  setup() {
    const dashboardComposable = useDashboard();
    const { defaultOrganization, isFacilityDefaultOrganization } = useUser();
    const { addAlert, addLocalAlert, clearAllAlerts, localAlerts } =
      useAlerts();

    return {
      snakeCase,
      addAlert,
      addLocalAlert,
      clearAllAlerts,
      localAlerts,
      isFacilityDefaultOrganization,
      defaultOrganization,
      ...dashboardComposable,
    };
  },
  data() {
    return {
      loading: false,
      selectedTaskType: "incident_resolution",
      selectedSensor: "motion",
      selectedPriority: "all",
      searchQuery: "",
      showPrioritySettings: false,
      priorities: [],
      priorityData: null,
      selectedIncidentType: null,
      selectedPriorityLevel: null,

      showTaskDialog: false,
      showNotificationDialog: false,
      selectedTaskRule: null,
      isEditing: false,
      selectedNotificationSettings: null,

      priorityFeatureId: null,
      escalationFeatureId: null,

      taskRules: [],
      taskTypes: [],

      showEscalationActions: false,
      escalationSaving: false,

      taskTypeOptions: [
        { text: "Incident Resolution", value: "incident_resolution" },
        { text: "Walkaround/Checklist", value: "walkaround_checklist" },
        { text: "Maintenance", value: "maintenance" },
        { text: "Manage Overlocks", value: "manage_overlocks" },
        { text: "Pre Install Devices", value: "pre_install_devices" },
        { text: "Unit Cleaning", value: "unit_cleaning" },
        { text: "Custom", value: "custom" },
      ],

      sensorOptions: [
        { text: "Motion", value: "motion" },
        { text: "Door", value: "entry" },
        { text: "Zone", value: "facility_motion" },
        { text: "Temperature", value: "temperature" },
        { text: "Humidity", value: "humidity" },
      ],

      priorityOptions: [
        { text: "All", value: "all" },
        { text: "Critical", value: "critical" },
        { text: "Severe", value: "severe" },
        { text: "Substantial", value: "substantial" },
        { text: "Moderate", value: "moderate" },
        { text: "Low", value: "low" },
      ],

      taskRuleHeaders: [
        { text: "Facility Name", value: "facilityName" },
        { text: "Task Code", value: "code" },
        { text: "Task Name", value: "taskName" },
        { text: "Task Description", value: "description" },
        { text: "Schedule", value: "schedule" },
        { text: "Detected Threshold Variables", value: "detectedThreshold" },
        { text: "Priority Level", value: "priority" },
        { text: "Status", value: "status" },
        { text: "Notification Preference", value: "notificationPreference" },
        { text: "Actions", value: "actions", sortable: false },
      ],

      organizationIds: [],
    };
  },

  computed: {
    fieldDisabled() {
      return this.defaultOrganization.type !== "region";
    },

    showSensorFilter() {
      return this.selectedTaskType === "incident_resolution";
    },

    filteredTaskRules() {
      let filtered = this.taskRules;

      if (this.selectedTaskType === "manage_overlocks") {
        const existingRules = this.taskRules
          ? this.taskRules.filter(
              (rule) => rule.taskType?.name === "manage_overlocks"
            )
          : [];

        const existingEvents = existingRules
          .filter((rule) => rule.conditions && rule.conditions.event)
          .map((rule) => rule.conditions.event);

        const availableDefaultRules = DEFAULT_MANAGE_OVERLOCKS_RULES.filter(
          (defaultRule) => {
            if (!defaultRule.conditions || !defaultRule.conditions.event) {
              return true;
            }
            return !existingEvents.includes(defaultRule.conditions.event);
          }
        );

        const mergedRules = [...availableDefaultRules];

        existingRules.forEach((existingRule) => {
          const defaultIndex = mergedRules.findIndex((defaultRule) => {
            if (
              existingRule.conditions?.event &&
              defaultRule.conditions?.event
            ) {
              return (
                defaultRule.conditions.event === existingRule.conditions.event
              );
            }
            return defaultRule.code === existingRule.code;
          });

          if (defaultIndex !== -1) {
            mergedRules[defaultIndex] = {
              ...existingRule,
              isDefault: false,
            };
          } else {
            mergedRules.push({
              ...existingRule,
              isDefault: false,
            });
          }
        });

        filtered = mergedRules;
      } else if (this.selectedTaskType !== "incident_resolution") {
        const taskTypeMapping = {
          walkaround_checklist: "walkaround_checklist",
          maintenance: "maintenance",
          unit_cleaning: "unit_cleaning",
          custom: "custom",
          pre_install_devices: "pre_install_devices",
        };

        const targetTaskTypeName = taskTypeMapping[this.selectedTaskType];
        if (targetTaskTypeName) {
          filtered = filtered.filter(
            (rule) => rule.taskType?.name === targetTaskTypeName
          );
        }
      }

      if (this.selectedPriority !== "all") {
        filtered = filtered.filter(
          (rule) => rule.priority?.toLowerCase() === this.selectedPriority
        );
      }

      return filtered;
    },
  },

  apollo: {
    organizationFeatures: {
      query: ORGANIZATION_FEATURES,
      variables() {
        return {
          id: this.defaultOrganization.id,
        };
      },
      fetchPolicy: "no-cache",
      update(data) {
        try {
          const organization = data.organization;
          const priorityFeature = organization.features.find(
            (f) => f.feature.name === "incident_priority_matrix"
          );
          const escalationFeature = organization.features.find(
            (f) => f.feature.name === "incident_escalation_matrix"
          );

          if (priorityFeature) {
            this.priorityFeatureId = priorityFeature.id;
          }
          if (escalationFeature) {
            this.escalationFeatureId = escalationFeature.id;
          }

          return organization.features;
        } catch (error) {
          console.error("Error processing organization features:", error);
          return [];
        }
      },
      error(error) {
        console.error("Error fetching organization features:", error);
      },
    },

    priorityMatrix: {
      query: GET_OVERRIDDEN_ORGANIZATION_FEATURE,
      variables() {
        return {
          featureName: "incident_priority_matrix",
          organizationId: this.defaultOrganization.id,
        };
      },
      fetchPolicy: "network-only",
      update(data) {
        const matrixData =
          data.overridenOrganizationFeature.config.incidentPriorityMatrix;

        this.priorityData = matrixData;

        this.priorities = matrixData.map((item) => ({
          level: item.level,
          name: item.name,
          title: item.name.charAt(0).toUpperCase() + item.name.slice(1),
          isOpen: false,
        }));

        return matrixData;
      },
      error(error) {
        console.error("Error fetching data:", error);
        this.addAlert({
          type: "error",
          message: "Error fetching priority settings.",
          timeout: 10,
        });
      },
    },

    taskRules: {
      query: GET_TASK_RULES,
      variables() {
        return {
          organizationIds: this.organizationIds,
        };
      },
      skip() {
        const shouldSkip = this.organizationIds.length === 0;
        return shouldSkip;
      },
      fetchPolicy: "cache-and-network",
      update(data) {
        return data.taskRules || [];
      },
      error(error) {
        console.error("Error fetching task rules:", error);
        this.addAlert({
          type: "error",
          message: "Error fetching task rules.",
          timeout: 10,
        });
      },
    },

    taskTypes: {
      query: TASK_TYPES,
      variables() {
        return {
          status: "active",
        };
      },
      fetchPolicy: "cache-and-network",
      update(data) {
        return data.taskTypes || [];
      },
      error(error) {
        console.error("Error fetching task types:", error);
      },
    },
  },
  watch: {
    selectedFacilities: {
      handler(newFacilities) {
        if (newFacilities !== undefined) {
          this.setOrganizationIds();
        }
      },
      deep: true,
    },

    selectedRegions: {
      handler(newRegions) {
        if (newRegions !== undefined) {
          this.setOrganizationIds();
        }
      },
      deep: true,
    },
  },

  async mounted() {
    await this.initializeOrganizations();
  },

  methods: {
    async initializeOrganizations() {
      try {
        await this.fetchOrganizations(
          this.$apollo,
          this.defaultOrganization.id,
          ["region", "facility", "organization"]
        );

        await this.$nextTick();

        this.setOrganizationIds();
      } catch (error) {
        console.error("Error initializing organizations:", error);
      }
    },

    setOrganizationIds() {
      const facilityIds = this.selectedFacilities?.length
        ? this.selectedFacilities.map((facility) => facility.id)
        : [];

      const regionIds = this.selectedRegions?.length
        ? this.selectedRegions.map((region) => region.id)
        : [];

      const organizationIds = [...facilityIds, ...regionIds];

      this.organizationIds =
        organizationIds.length > 0
          ? organizationIds
          : [this.defaultOrganization.id];

      if (this.organizationIds.length > 0) {
        this.$apollo.queries.taskRules.refetch();
      }
    },

    handleTaskTypeChange() {
      if (this.selectedTaskType === "incident_resolution") {
        this.selectedSensor = "motion";
      }
      this.showEscalationActions = false;
      this.showPrioritySettings = false;
    },

    togglePrioritySettings() {
      this.showPrioritySettings = !this.showPrioritySettings;
    },

    toggleAccordion(name) {
      this.priorities = this.priorities.map((priority) => ({
        ...priority,
        isOpen: priority.name === name ? !priority.isOpen : false,
      }));
    },

    getSettingsForPriority(name) {
      if (!this.priorityData) return {};
      return this.priorityData.find((item) => item.name === name) || {};
    },

    handleViewAssignedIncidents(incidentType, priorityLevel) {
      this.selectedIncidentType = this.snakeCase(incidentType);
      this.selectedPriorityLevel = priorityLevel.toLowerCase();
      this.showPrioritySettings = false;
    },

    async handleSaveSuccess() {
      this.addAlert({
        type: "success",
        message: "Priority settings saved successfully",
        timeout: 5,
      });
      try {
        await this.$apollo.queries.priorityMatrix.refetch();
      } catch (error) {
        console.error("Error refetching data:", error);
      }
    },

    handleSaveError() {
      this.addAlert({
        type: "error",
        message: "Error occurred while saving priority settings.",
        timeout: 5,
      });
    },

    getFacilityName(taskRule) {
      if (!taskRule.organization) return "All Facilities";

      const facility = taskRule.organization.facility?.[0];
      if (facility) {
        return facility.name;
      }

      return taskRule.organization.name || "All Facilities";
    },

    async toggleTaskRule(taskRule) {
      try {
        const newStatus = taskRule.status === "active" ? "inactive" : "active";

        if (taskRule.isDefault && newStatus === "active") {
          await this.createTaskRuleFromDefault(taskRule);
        } else if (!taskRule.isDefault) {
          await this.$apollo.mutate({
            mutation: UPDATE_TASK_RULE,
            variables: {
              id: taskRule.id,
              status: newStatus,
            },
          });

          taskRule.status = newStatus;
        }

        this.addAlert({
          type: "success",
          message: `Task rule ${
            newStatus === "active" ? "activated" : "deactivated"
          } successfully`,
          timeout: 5,
        });

        await this.$apollo.queries.taskRules.refetch();
      } catch (error) {
        console.error("Error toggling task rule:", error);
        this.addAlert({
          type: "error",
          message: "Error updating task rule status",
          timeout: 5,
        });
      }
    },

    async createTaskRuleFromDefault(defaultRule) {
      try {
        const taskType = this.taskTypes.find(
          (type) => type.name === "manage_overlocks"
        );

        if (!taskType) {
          throw new Error("Manage Overlocks task type not found");
        }

        let regionId = this.defaultOrganization.id;
        if (this.defaultOrganization.type === "facility") {
          regionId =
            this.defaultOrganization.parentId || this.defaultOrganization.id;
        }

        const getDefaultNotificationPreference = () => {
          return {
            notifyAssignment: {
              email: true,
              sms: false,
              managerRole: "facility_manager",
              robocall: false,
              slack: false,
              teams: false,
              summaryReportingSettings: {
                dailyConcerningActivity: false,
                dailyFacilitySummary: false,
                weeklyFacilitySummary: false,
                weeklyRegionalSummary: false,
              },
            },
            notifyTodo: {
              email: true,
              sms: false,
              managerRole: "facility_manager",
              robocall: false,
              slack: false,
              teams: false,
              summaryReportingSettings: {
                dailyConcerningActivity: false,
                dailyFacilitySummary: false,
                weeklyFacilitySummary: false,
                weeklyRegionalSummary: false,
              },
            },
            notifyOverdue: [
              {
                email: true,
                sms: false,
                managerRole: "facility_manager",
                robocall: false,
                slack: false,
                teams: false,
                summaryReportingSettings: {
                  dailyConcerningActivity: false,
                  dailyFacilitySummary: false,
                  weeklyFacilitySummary: false,
                  weeklyRegionalSummary: false,
                },
              },
              {
                email: true,
                sms: false,
                managerRole: "regional_manager",
                robocall: false,
                slack: false,
                teams: false,
                summaryReportingSettings: {
                  dailyConcerningActivity: false,
                  dailyFacilitySummary: false,
                  weeklyFacilitySummary: false,
                  weeklyRegionalSummary: false,
                },
              },
              {
                email: true,
                sms: false,
                managerRole: "organization_manager",
                robocall: false,
                slack: false,
                teams: false,
                summaryReportingSettings: {
                  dailyConcerningActivity: false,
                  dailyFacilitySummary: false,
                  weeklyFacilitySummary: false,
                  weeklyRegionalSummary: false,
                },
              },
            ],
          };
        };

        const taskRuleData = {
          name: defaultRule.name,
          description: defaultRule.description,
          status: "active",
          priority: defaultRule.priority,
          taskTypeId: taskType.id,
          organizationId: regionId,
          startsAt: new Date().toISOString(),
          endsAt: new Date("2099-12-31").toISOString(),
          frequency: {
            type: "daily",
            interval: 1,
          },
          actions: {
            photoVideoRequired: false,
          },
          notificationPreference: getDefaultNotificationPreference(),
          conditions: defaultRule.conditions || {
            event:
              defaultRule.code === "MNG-OL-AL"
                ? "add_overlock"
                : "remove_overlock",
          },
        };

        await this.$apollo.mutate({
          mutation: CREATE_TASK_RULE,
          variables: taskRuleData,
        });
      } catch (error) {
        console.error("Error creating task rule from default:", error);
        throw error;
      }
    },

    async handleTaskSave(taskRuleData) {
      try {
        this.loading = true;

        if (this.isEditing) {
          await this.$apollo.mutate({
            mutation: UPDATE_TASK_RULE,
            variables: taskRuleData,
          });

          this.addAlert({
            type: "success",
            message: "Task rule updated successfully",
            timeout: 5,
          });
        } else {
          await this.$apollo.mutate({
            mutation: CREATE_TASK_RULE,
            variables: taskRuleData,
          });

          this.addAlert({
            type: "success",
            message: "Task rule created successfully",
            timeout: 5,
          });
        }

        this.handleTaskDialogClose();
        await this.$apollo.queries.taskRules.refetch();
      } catch (error) {
        console.error("Error saving task rule:", error);
        this.addAlert({
          type: "error",
          message: `Error ${
            this.isEditing ? "updating" : "creating"
          } task rule: ${error.message}`,
          timeout: 10,
        });
      } finally {
        this.loading = false;
      }
    },

    handleTaskDialogClose() {
      this.showTaskDialog = false;
      this.$nextTick(() => {
        this.selectedTaskRule = null;
        this.isEditing = false;
      });
    },

    viewNotificationSettings(taskRule) {
      let notificationPreference = taskRule.notificationPreference;

      if (typeof notificationPreference === "string") {
        try {
          notificationPreference = JSON.parse(notificationPreference);
        } catch (error) {
          console.error("Error parsing notification preference:", error);
          notificationPreference = {};
        }
      }

      this.selectedNotificationSettings =
        notificationPreference && typeof notificationPreference === "object"
          ? { ...notificationPreference }
          : {};

      this.$nextTick(() => {
        this.showNotificationDialog = true;
      });
    },

    formatSchedule(frequency) {
      if (!frequency) return "Automatic";

      const freq =
        typeof frequency === "string" ? JSON.parse(frequency) : frequency;

      switch (freq.type) {
        case "one-time":
          return "One-Time";
        case "daily":
          return freq.interval === 1 ? "Daily" : `Every ${freq.interval} days`;
        case "weekly":
          const days = freq.daysOfWeek?.join(", ") || "Monday";
          return freq.interval === 1
            ? `Weekly (${titleCase(days)})`
            : `Every ${freq.interval} weeks (${days})`;
        case "monthly":
          const dayOfMonth = freq.dayOfMonth || 1;
          return freq.interval === 1
            ? `Monthly (${dayOfMonth} of month)`
            : `Every ${freq.interval} months (${dayOfMonth} of month)`;
        case "yearly":
          const monthNames = [
            "",
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
          ];
          const month = monthNames[freq.monthOfYear] || "Jan";
          return freq.interval === 1
            ? `Yearly (${month})`
            : `Every ${freq.interval} years (${month})`;
        case "custom":
          return this.formatCustomSchedule(freq);
        default:
          return "Custom";
      }
    },
    formatCustomSchedule(frequency) {
      const { interval, daysOfWeek, dayOfMonth, monthOfYear } = frequency;

      if (daysOfWeek && daysOfWeek.length > 0) {
        const days = daysOfWeek.join(", ");
        return `Custom ${interval} weeks (${titleCase(days)})`;
      }

      if (dayOfMonth) {
        return `Custom ${interval} months (${dayOfMonth} of month)`;
      }

      if (monthOfYear) {
        const monthNames = [
          "",
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        const month = monthNames[monthOfYear] || "Jan";
        return `Custom ${interval} years (${month})`;
      }

      return `Custom ${interval} days`;
    },

    formatDetectedThreshold(frequency) {
      if (!frequency) {
        return "Automatic";
      }

      const freq =
        typeof frequency === "string" ? JSON.parse(frequency) : frequency;

      switch (freq.type) {
        case "daily":
          return "Start of Office Hours";
        case "weekly":
          const days = freq.daysOfWeek?.join(", ") || "Monday";
          return `${days}`;
        case "monthly":
          return `${freq.dayOfMonth || 1} Of Month`;
        case "yearly":
          const monthNames = [
            "",
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
          ];
          return monthNames[freq.monthOfYear] || "January";
        case "custom":
          return this.formatCustomThreshold(freq);
        default:
          return "Custom Rule";
      }
    },

    formatCustomThreshold(frequency) {
      const { interval, daysOfWeek, dayOfMonth, monthOfYear } = frequency;

      if (daysOfWeek && daysOfWeek.length > 0) {
        return daysOfWeek.join(", ");
      }

      if (dayOfMonth) {
        return `${dayOfMonth} Of Month`;
      }

      if (monthOfYear) {
        const monthNames = [
          "",
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ];
        return monthNames[monthOfYear] || "January";
      }

      return "Custom Interval";
    },

    formatScheduleForItem(item) {
      if (item.taskType?.name === "manage_overlocks") {
        return "Automatic";
      }

      return this.formatSchedule(item.frequency);
    },

    formatDetectedThresholdForItem(item) {
      if (item.taskType?.name === "manage_overlocks") {
        const defaultRule = DEFAULT_MANAGE_OVERLOCKS_RULES.find((rule) => {
          if (item.conditions?.event && rule.conditions?.event) {
            return rule.conditions.event === item.conditions.event;
          }
          if (item.name === rule.name) {
            return true;
          }
          return false;
        });

        return defaultRule ? defaultRule.detectedThreshold : "Automatic";
      }

      return this.formatDetectedThreshold(item.frequency);
    },

    handleEscalationChanges(hasChanges) {
      this.showEscalationActions = hasChanges;
    },

    handleEscalationSave() {
      if (this.$refs.escalationMatrix) {
        this.escalationSaving = true;
        this.$refs.escalationMatrix.handleSave();
      }
    },

    handleEscalationCancel() {
      if (this.$refs.escalationMatrix) {
        this.$refs.escalationMatrix.handleCancel();
        this.showEscalationActions = false;
      }
    },

    handleEscalationSaveSuccess() {
      this.escalationSaving = false;
      this.showEscalationActions = false;
    },

    handleEscalationSaveError(error) {
      this.escalationSaving = false;
    },
    openAddTaskDialog() {
      this.selectedTaskRule = null;
      this.isEditing = false;
      this.showTaskDialog = true;
    },

    editTaskRule(taskRule) {
      this.selectedTaskRule = { ...taskRule };
      this.isEditing = true;
      this.showTaskDialog = true;
    },

    handleTaskSaved(data) {
      this.$apollo.queries.taskRules.refetch();
    },

    handleShowAlert(alert) {
      this.addLocalAlert(alert);
      this.addAlert(alert);
    },

    titleCase,
    toUpperCase,
    getColorBySeverity,
  },
};
</script>

<style scoped>
.v-card {
  box-shadow: none !important;
}

.accordion-header {
  cursor: pointer;
  background-color: #f5f5f5;
  padding: 12px 16px;
  border-radius: 4px;
}

.accordion-content {
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-top: none;
  margin-bottom: 8px;
}
</style>
```
